<template>
  <div class="entity-management-panel" @click="clearSelectedEntity">
    <!-- Entity Groups -->
    <div class="entity-groups">
      <!-- Group entities by type -->
      <div v-for="(groupEntities, type) in groupedEntities" :key="type" class="entity-group">
        <h3 class="entity-group-title">{{ formatEntityType(type) }}</h3>
        
        <!-- Entity Cards -->
        <div class="entity-cards">
          <div v-for="entity in groupEntities" 
               :key="entity.id" 
               class="entity-card"
               :class="{'entity-card--expanded': isEntityExpanded(entity.id)}">
            
            <!-- Entity Header -->
            <div class="entity-header">
              <div class="entity-info" @click.stop="toggleEntityExpanded(entity.id)">
                <div class="entity-title">
                  <h4>{{ entity.name }}</h4>
                  <span class="entity-subtitle">{{ entity.type }}</span>
                  <div class="source-count" v-if="entityFields(entity.id)?.length">
                    {{ entityFields(entity.id).length }} sources
                  </div>
                  <div v-if="entitySections(entity.id)?.length" class="entity-sections">
                    <span v-for="section in entitySections(entity.id)" 
                          :key="section" 
                          class="section-tag">
                      {{ section }}
                    </span>
                  </div>
                </div>
              </div>
              <div class="entity-actions">
                <button 
                  v-if="canMergeWithOthers(entity)"
                  class="merge-button" 
                  type="button"
                  :class="{ 'is-active': selectedEntity?.id === entity.id }"
                  @click.stop="toggleMergeView(entity)">
                  <i class="fas fa-code-merge"></i>
                  Merge
                </button>
                <i :class="isEntityExpanded(entity.id) ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"
                   @click.stop="toggleEntityExpanded(entity.id)"></i>
              </div>
            </div>

            <!-- Entity Content -->
            <div v-if="isEntityExpanded(entity.id)" class="entity-content">
              <!-- Fields Section -->
              <div v-if="Object.keys(entityFields(entity.id)).length" class="entity-section">
                <div class="section-header">
                  <h4>Fields</h4>
                </div>
                <div class="section-content">
                  <div v-for="(sourceFields, sourceName) in entityFields(entity.id)" 
                       :key="sourceName" 
                       class="source-group">
                    <div class="source-header">
                      <h5>{{ sourceName }}</h5>
                      <span class="source-date" v-if="sourceFields[0]?.source_date">
                        {{ formatDate(sourceFields[0].source_date) }}
                      </span>
                    </div>
                    <div class="source-fields">
                      <div v-for="field in sourceFields" 
                           :key="field.id" 
                           class="field-item">
                        <div class="field-label">{{ field.label || field.normalized_key }}</div>
                        <div class="field-value">{{ field.value || '(empty)' }}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- No Fields Message -->
              <div v-else class="no-sources">
                No fields available for this entity
              </div>
            </div>

            <!-- Merge View -->
            <div v-if="selectedEntity?.id === entity.id" class="merge-view">
              <div class="merge-candidates">
                <div v-for="candidate in mergeCandidates"
                     :key="candidate.id"
                     class="merge-candidate"
                     @click="selectMergeTarget(entity, candidate)">
                  <div class="candidate-preview">
                    <div class="candidate-header">
                      <span class="candidate-name">{{ candidate.name }}</span>
                      <span class="source-count" v-if="entityFields(candidate.id)?.length">
                        {{ entityFields(candidate.id).length }} sources
                      </span>
                    </div>
                    <div class="candidate-sections" v-if="entitySections(candidate.id)?.length">
                      <span v-for="section in entitySections(candidate.id)" 
                            :key="section" 
                            class="section-tag">
                        {{ section }}
                      </span>
                    </div>
                    <div class="candidate-sources" v-if="entityFields(candidate.id)?.length">
                      <div v-for="source in entityFields(candidate.id)" 
                           :key="source.id" 
                           class="source-preview">
                        <span class="source-name">{{ source.source_name }}</span>
                        <span class="source-date">{{ formatDate(source.source_date) }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Add Oruga Modal for merge review -->
    <o-modal v-model="active" :can-cancel="['escape', 'outside']" :width="960" class="merge-modal">
      <template #header>
        <h3 class="modal-card-title">Review Merge</h3>
      </template>
      
      <div v-if="mergeReviewData" class="merge-review">
        <div class="merge-entities-info">
          <p>Merging {{ mergeReviewData.sourceEntity.name }} into {{ mergeReviewData.targetEntity.name }}</p>
        </div>
        <div class="merge-fields" v-if="mergeReviewData.fields.length">
          <div v-for="field in mergeReviewData.fields" :key="field.key" class="merge-field">
            <h4>{{ field.label }}</h4>
            <div class="merge-values">
              <div v-for="(value, source) in field.values" 
                   :key="source"
                   class="merge-value-option"
                   :class="{'selected': field.selectedValue === value}"
                   @click="selectMergeValue(field, value)">
                <div class="value-content">{{ value }}</div>
                <div class="value-source">from {{ source }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <template #footer>
        <div class="merge-actions">
          <o-button @click="closeMergeReview">Cancel</o-button>
          <o-button @click="confirmMerge" variant="primary">Confirm Merge</o-button>
        </div>
      </template>
    </o-modal>
  </div>
</template>

<script>
import Vue from 'vue';
import { Oruga } from '@oruga-ui/oruga';
import { Modal, Button } from '@oruga-ui/oruga';
import '@oruga-ui/oruga/dist/oruga.css';
import { useFormDataStore } from '../store/formData';

// Register Oruga globally
// Vue.use(Oruga);

// // Register specific components
// Vue.component('o-modal', Modal);
// Vue.component('o-button', Button);

export default {
  name: 'EntityManagementPanel',
  
  components: {
    // Components are registered globally via Vue.use
  },

  data() {
    return {
      store: useFormDataStore(),
      expandedEntityIds: [],
      showMergeReviewModal: false,
      mergeReviewData: null,
      selectedEntity: null,
      active: false,
    };
  },

  computed: {
    entities() {
      return Object.values(this.store.entities);
    },

    entityFields() {
      return (entityId) => {
        const fields = this.store.getEntityFields(entityId) || [];
        // Group fields by source
        return fields.reduce((acc, field) => {
          const sourceKey = field.source_name || 'Unknown Source';
          if (!acc[sourceKey]) {
            acc[sourceKey] = [];
          }
          acc[sourceKey].push(field);
          return acc;
        }, {});
      };
    },

    sectionEntityMapping() {
      return this.store.sectionEntityMapping;
    },

    processingSections() {
      return this.store.processingSections;
    },

    highlightedSection() {
      return this.store.highlightedSection;
    },

    entitySections() {
      return this.store.entitySections;
    },

    sectionFields() {
      return this.store.sectionFields;
    },

    groupedEntities() {
      return this.entities.reduce((groups, entity) => {
        const normalizedType = this.formatEntityType(entity.type || 'Other');
        if (!groups[normalizedType]) {
          groups[normalizedType] = [];
        }
        groups[normalizedType].push(entity);
        return groups;
      }, {});
    },

    mergeCandidates() {
      if (!this.selectedEntity) return [];
      const normalizedType = this.formatEntityType(this.selectedEntity.type);
      return this.entities.filter(entity => 
        entity.id !== this.selectedEntity.id && 
        this.formatEntityType(entity.type) === normalizedType,
      );
    },

    sections() {
      return Object.keys(this.sectionEntityMapping);
    },
  },

  methods: {
    toggleMergeView(entity) {
      if (this.selectedEntity?.id === entity.id) {
        this.selectedEntity = null;
        this.mergeReviewData = null;
      } else {
        this.selectedEntity = entity;
        this.mergeReviewData = null;
      }
    },

    clearSelectedEntity() {
      this.selectedEntity = null;
      this.mergeReviewData = null;
    },

    formatEntityType(type) {
      if (!type) return 'Other';
      return type
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    },

    toggleEntityExpanded(entityId) {
      const index = this.expandedEntityIds.indexOf(entityId);
      if (index > -1) {
        this.expandedEntityIds.splice(index, 1);
      } else {
        this.expandedEntityIds.push(entityId);
      }
    },

    isEntityExpanded(entityId) {
      return this.expandedEntityIds.includes(entityId);
    },

    formatDate(dateString) {
      if (!dateString) return '';
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
      } catch (error) {
        return dateString;
      }
    },

    canMergeWithOthers(entity) {
      if (!entity || !entity.type) return false;
      const normalizedType = this.formatEntityType(entity.type);
      return this.entities.some(e => 
        e.id !== entity.id && 
        this.formatEntityType(e.type) === normalizedType,
      );
    },

    async handleSectionClick(sectionId, entityId) {
      try {
        // Only handle manual section-entity mapping
        if (entityId) {
          await this.store.mapSectionToEntityAPI({ sectionId, entityId });
          this.$emit('section-mapped', { sectionId, entityId });
        }
      } catch (error) {
        console.error('Error mapping section to entity:', error);
        this.$buefy.toast.open({
          message: 'Error mapping section to entity',
          type: 'is-danger',
        });
      }
    },

    async handleFieldValueChange(fieldId, value) {
      try {
        await this.store.updateFieldValueAPI({ fieldId, value });
        this.$emit('field-value-updated', { fieldId, value });
      } catch (error) {
        console.error('Error updating field value:', error);
        this.$buefy.toast.open({
          message: 'Error updating field value',
          type: 'is-danger',
        });
      }
    },

    async selectMergeTarget(sourceEntity, targetEntity) {
      if (!sourceEntity || !targetEntity) {
        console.warn('Invalid entities for merge:', { sourceEntity, targetEntity });
        return;
      }

      try {
        // Get merge fields from store
        const fields = this.store.prepareMergeFields(sourceEntity.id, targetEntity.id);

        if (fields.length === 0) {
          console.warn('No fields to merge');
          return;
        }

        // Create selectedValues object with default values
        const selectedValues = {};
        fields.forEach(field => {
          // Default to the target entity's value if available, otherwise source entity's value
          const targetValue = field.values[targetEntity.name];
          const sourceValue = field.values[sourceEntity.name];
          selectedValues[field.key] = targetValue || sourceValue;
        });

        // Perform the merge in the store
        await this.store.mergeEntities({
          sourceEntityId: sourceEntity.id,
          targetEntityId: targetEntity.id,
          selectedValues,
        });

        // Emit success event
        this.$emit('entity-merged', {
          sourceEntity,
          targetEntity,
        });

        // Clear selection
        this.selectedEntity = null;
        this.mergeReviewData = null;
      } catch (error) {
        console.error('Error during merge:', error);
      }
    },

    selectMergeValue(field, value) {
      field.selectedValue = value;
    },

    closeMergeReview() {
      this.active = false;
      // Clear the data after the transition completes
      setTimeout(() => {
        this.mergeReviewData = null;
      }, 300);
    },

    async confirmMerge() {
      if (!this.mergeReviewData?.sourceEntity || !this.mergeReviewData?.targetEntity) {
        console.warn('Invalid merge data:', this.mergeReviewData);
        this.closeMergeReview();
        return;
      }

      try {
        // Get selected values
        const selectedValues = {};
        this.mergeReviewData.fields.forEach(field => {
          if (field.selectedValue !== null) {
            selectedValues[field.key] = field.selectedValue;
          }
        });

        // Perform the merge in the store
        await this.store.mergeEntities({
          sourceEntityId: this.mergeReviewData.sourceEntity.id,
          targetEntityId: this.mergeReviewData.targetEntity.id,
          selectedValues,
        });

        // Emit success event
        this.$emit('entity-merged', {
          sourceEntity: this.mergeReviewData.sourceEntity,
          targetEntity: this.mergeReviewData.targetEntity,
        });

        // Close the review
        this.closeMergeReview();
      } catch (error) {
        console.error('Error during merge:', error);
        // You might want to show an error message to the user here
      }
    },
  },
};
</script>

<style>
/* Remove the Oruga dropdown styles */
/* Add new merge view styles */
.merge-view {
  padding: 1rem;
  background: var(--stk-text-15);
  border-top: 1px solid var(--stk-border);
}

.merge-candidates {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
}

.merge-candidate {
  background: var(--stk-text-20);
  border: 1px solid var(--stk-border);
  border-radius: var(--stk-border-radius);
  transition: all 0.2s ease;
  cursor: pointer;
  overflow: hidden;
}

.merge-candidate:hover {
  border-color: var(--stk-primary);
  background: var(--stk-text-25);
  min-width: 300px;
}

.candidate-preview {
  padding: 1rem;
}

.candidate-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  gap: 1rem;
}

.candidate-name {
  font-weight: 600;
  color: var(--stk-text-strong);
  font-size: 1rem;
  flex: 1;
  min-width: 0;
  word-wrap: break-word;
  white-space: normal;
  line-height: 1.4;
}

.candidate-sources {
  background: var(--stk-text-15);
  border-radius: var(--stk-border-radius);
  padding: 0.5rem;
}

.source-preview {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: var(--stk-text-25);
  border-radius: var(--stk-border-radius);
  margin-bottom: 0.5rem;
  gap: 1rem;
  border: 1px solid var(--stk-border);
}

.source-preview:last-child {
  margin-bottom: 0;
}

.source-preview .source-name {
  color: var(--stk-text-inverse);
  font-weight: 600;
  font-size: 0.9rem;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.source-preview .source-date {
  color: var(--stk-text-inverse);
  opacity: 0.8;
  font-size: 0.875rem;
  flex-shrink: 0;
}

.merge-review {
  padding: 1rem;
  background: var(--stk-text-15);
  border-top: 1px solid var(--stk-border);
}

.merge-entities-info {
  background: var(--stk-text-20);
  padding: 1rem;
  border-radius: var(--stk-border-radius);
  margin-bottom: 1rem;
}

.merge-fields {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
}

.merge-field {
  background: var(--stk-text-20);
  border: 1px solid var(--stk-border);
  border-radius: var(--stk-border-radius);
  padding: 1rem;
}

.merge-values {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.merge-value-option {
  padding: 0.75rem;
  background: var(--stk-text-15);
  border: 1px solid var(--stk-border);
  border-radius: var(--stk-border-radius);
  cursor: pointer;
  transition: all 0.2s ease;
}

.merge-value-option:hover {
  border-color: var(--stk-primary);
  background: var(--stk-primary-10);
}

.merge-value-option.selected {
  border-color: var(--stk-primary);
  background: var(--stk-primary-20);
}

.merge-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid var(--stk-border);
}

/* Add modal-specific styles */
.merge-modal {
  max-width: 800px;
  width: 90%;
}

.merge-modal :deep(.modal-card) {
  max-width: 800px;
  width: 90%;
  margin: 0 auto;
}

.merge-modal :deep(.modal-card-body) {
  max-height: 70vh;
  overflow-y: auto;
  padding: 1.5rem;
}

.merge-modal :deep(.modal-card-foot) {
  padding: 1rem 1.5rem;
  background: var(--stk-text-15);
  border-top: 1px solid var(--stk-border);
}

.merge-modal :deep(.modal-card-head) {
  padding: 1rem 1.5rem;
  background: var(--stk-text-15);
  border-bottom: 1px solid var(--stk-border);
}

.merge-modal :deep(.modal-card-title) {
  color: var(--stk-text-strong);
  font-weight: 600;
  font-size: 1.25rem;
}
</style>

<style scoped>
.entity-management-panel {
  padding: 1rem;
  background: var(--stk-text-15);
  height: 100%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  position: relative;
}

.entity-groups {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

.entity-group {
  margin-bottom: 2rem;
}

.entity-group-title {
  font-size: 1.2rem;
  color: var(--stk-text-strong);
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid var(--stk-border);
}

.entity-cards {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-height: 0;
}

.entity-card {
  background: var(--stk-text-20);
  border-radius: var(--stk-border-radius);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: visible !important;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  position: relative;
  isolation: isolate;
}

.entity-header {
  position: relative;
  z-index: 3;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 1rem;
  background: var(--stk-text-20);
  border-bottom: 1px solid var(--stk-border);
  min-height: 72px;
  gap: 1rem;
}

.entity-info {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  cursor: pointer;
  flex: 1;
  min-width: 0;
}

.entity-title {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 0;
}

.entity-title h4 {
  margin: 0;
  color: var(--stk-text-strong);
  font-size: 1.25rem;
  font-weight: 600;
  min-width: 0;
  word-wrap: break-word;
  white-space: normal;
  line-height: 1.4;
}

.entity-subtitle {
  color: var(--stk-text-weak);
  font-size: 0.875rem;
}

.source-count {
  background: var(--stk-primary-20);
  color: var(--stk-primary);
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 500;
  width: fit-content;
}

.entity-sections {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.25rem;
}

.section-tag {
  background: var(--stk-primary-20);
  color: var(--stk-primary);
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 500;
}

.entity-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-shrink: 0;
  padding-top: 0.25rem;
}

.merge-button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: var(--stk-primary);
  color: var(--stk-text-inverse);
  border: none;
  border-radius: var(--stk-border-radius);
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  white-space: nowrap;
  min-width: fit-content;
  font-weight: 500;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.merge-button:hover,
.merge-button.is-active {
  background: var(--stk-primary-dark);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.merge-button:active {
  background: var(--stk-primary-darker);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.entity-content {
  position: relative;
  z-index: 1;
  padding: 1rem;
  background: var(--stk-text-15);
  overflow-y: auto;
}

.entity-section {
  margin-bottom: 1rem;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background: var(--stk-text-20);
  border-radius: var(--stk-border-radius);
  border: 1px solid var(--stk-border);
  margin-bottom: 0.5rem;
}

.section-header h4 {
  margin: 0;
  color: var(--stk-text-strong);
  font-weight: 600;
  font-size: 1rem;
}

.section-content {
  padding: 1rem;
  background: var(--stk-text-15);
  border-radius: var(--stk-border-radius);
  margin-top: 0.5rem;
  border: 1px solid var(--stk-border);
}

.source-item {
  margin-bottom: 1rem;
  padding: 1rem;
  background: var(--stk-text-20);
  border-radius: var(--stk-border-radius);
}

.source-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.source-name {
  font-weight: 600;
  color: var(--stk-text-strong);
}

.source-date {
  color: var(--stk-text-weak);
  font-size: 0.9rem;
}

.field-item {
  display: flex;
  flex-direction: column;
  padding: 0.75rem;
  border-bottom: 1px solid var(--stk-border);
  background: var(--stk-text-20);
  margin-bottom: 0.5rem;
  border-radius: var(--stk-border-radius);
  gap: 0.5rem;
}

.field-item:last-child {
  margin-bottom: 0;
  border-bottom: none;
}

.field-label {
  color: var(--stk-text-weak);
  font-size: 0.9rem;
  word-break: break-word;
}

.field-value {
  color: var(--stk-text-strong);
  font-weight: 500;
  word-break: break-word;
}

.merge-selector {
  padding: 1rem;
}

.merge-selector-info {
  color: var(--stk-text-weak);
  background: var(--stk-text-15);
  padding: 0.75rem;
  border-radius: var(--stk-border-radius);
  margin-bottom: 1rem;
}

.merge-candidates {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-height: 60vh;
  overflow-y: auto;
}

.merge-candidate {
  background: var(--stk-text-20);
  padding: 1rem;
  border: 1px solid var(--stk-border);
  border-radius: var(--stk-border-radius);
  cursor: pointer;
  transition: all 0.2s ease;
}

.merge-candidate:hover {
  border-color: var(--stk-primary);
  background: var(--stk-primary-10);
}

.candidate-info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.candidate-info h4 {
  margin: 0;
  font-size: 1rem;
}

.candidate-preview {
  padding: 0.5rem;
}

.candidate-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
}

.candidate-name {
  font-weight: 600;
  color: var(--stk-text-strong);
  font-size: 1rem;
  flex: 1;
  min-width: 0;
  word-wrap: break-word;
  white-space: normal;
  line-height: 1.4;
}

.source-count {
  background: var(--stk-primary-20);
  color: var(--stk-primary);
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 500;
}

.candidate-sections {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 0.5rem 0;
}

.section-tag {
  background: var(--stk-primary-10);
  color: var(--stk-primary);
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 500;
}

.candidate-sources {
  background: var(--stk-text-10);
  border-radius: var(--stk-border-radius);
  padding: 0.5rem;
}

.source-preview {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: var(--stk-text-25);
  border-radius: var(--stk-border-radius);
  margin-bottom: 0.5rem;
  gap: 1rem;
  border: 1px solid var(--stk-border);
}

.source-preview:last-child {
  margin-bottom: 0;
}

.source-preview .source-name {
  color: var(--stk-text-inverse);
  font-weight: 600;
  font-size: 0.9rem;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.source-preview .source-date {
  color: var(--stk-text-inverse);
  opacity: 0.8;
  font-size: 0.875rem;
  flex-shrink: 0;
}

.merge-button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: var(--stk-primary);
  color: var(--stk-text-inverse);
  border: none;
  border-radius: var(--stk-border-radius);
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  white-space: nowrap;
  min-width: fit-content;
  font-weight: 500;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.merge-button:hover,
.merge-button.is-active {
  background: var(--stk-primary-dark);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.merge-button:active {
  background: var(--stk-primary-darker);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.merge-button i {
  font-size: 0.9rem;
}

.merge-review {
  padding: 1rem;
}

.merge-entities-info {
  background: var(--stk-text-15);
  padding: 1rem;
  border-radius: var(--stk-border-radius);
  margin-bottom: 1rem;
}

.merge-entities-info p {
  margin: 0;
  color: var(--stk-text-strong);
  font-weight: 500;
}

.merge-fields {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.merge-field {
  border: 1px solid var(--stk-border);
  border-radius: var(--stk-border-radius);
  padding: 1rem;
}

.merge-field h4 {
  margin: 0 0 1rem 0;
  color: var(--stk-text-strong);
}

.merge-values {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.merge-value-option {
  padding: 0.75rem;
  border: 1px solid var(--stk-border);
  border-radius: var(--stk-border-radius);
  cursor: pointer;
  transition: all 0.2s ease;
}

.merge-value-option:hover {
  border-color: var(--stk-primary);
  background: var(--stk-primary-10);
}

.merge-value-option.selected {
  border-color: var(--stk-primary);
  background: var(--stk-primary-20);
}

.value-content {
  color: var(--stk-text-strong);
  font-weight: 500;
}

.value-source {
  color: var(--stk-text-weak);
  font-size: 0.8rem;
  margin-top: 0.25rem;
}

.no-sources {
  color: var(--stk-text-weak);
  text-align: center;
  padding: 1rem;
  font-style: italic;
  background: var(--stk-text-15);
  border-radius: var(--stk-border-radius);
}

.dialog-content {
  margin: 0;
  padding: 0;
  width: 100%;
  max-width: 600px;
}

.modal-card {
  max-width: 600px !important;
  margin: 0 auto !important;
  width: 100% !important;
  border-radius: var(--stk-border-radius);
  overflow: hidden;
}

.modal-card-body {
  max-height: 70vh;
  overflow-y: auto;
}

:deep(.dialog .modal-card) {
  margin: 0;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

:deep(.dialog .modal-card-body) {
  flex: 1;
  overflow-y: auto;
}

:deep(.dialog .modal-card-foot) {
  background: var(--stk-text-15);
  justify-content: flex-end;
  gap: 0.75rem;
  border-top: 1px solid var(--stk-border);
}

:deep(.modal-card-head) {
  background: var(--stk-text-15);
  border-bottom: 1px solid var(--stk-border);
}

:deep(.modal-card-title) {
  color: var(--stk-text-strong);
  font-weight: 600;
}

.merge-dropdown {
  position: relative;
}

.entity-sections {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.section-tag {
  background: var(--stk-primary-20);
  color: var(--stk-primary);
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 500;
}

.candidate-sections {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 0.5rem 0;
}

.section-item {
  margin-bottom: 1rem;
  padding: 1rem;
  background: var(--stk-text-15);
  border-radius: var(--stk-border-radius);
  border: 1px solid var(--stk-border);
}

.section-item .section-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  padding: 0.5rem;
  background: var(--stk-text-20);
  border-radius: var(--stk-border-radius);
}

.section-name {
  font-weight: 600;
  color: var(--stk-text-strong);
  font-size: 1rem;
}

.section-fields {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.source-group {
  margin-bottom: 1.5rem;
  background: var(--stk-text-15);
  border-radius: var(--stk-border-radius);
  overflow: hidden;
}

.source-group:last-child {
  margin-bottom: 0;
}

.source-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background: var(--stk-text-20);
  border-bottom: 1px solid var(--stk-border);
}

.source-header h5 {
  margin: 0;
  color: var(--stk-text-strong);
  font-weight: 600;
  font-size: 0.9rem;
}

.source-date {
  color: var(--stk-text-weak);
  font-size: 0.8rem;
}

.source-fields {
  padding: 0.5rem;
}

.field-item {
  display: flex;
  flex-direction: column;
  padding: 0.75rem;
  border-bottom: 1px solid var(--stk-border);
  background: var(--stk-text-20);
  margin-bottom: 0.5rem;
  border-radius: var(--stk-border-radius);
  gap: 0.5rem;
}

.field-item:last-child {
  margin-bottom: 0;
  border-bottom: none;
}

.field-label {
  color: var(--stk-text-weak);
  font-size: 0.9rem;
  word-break: break-word;
}

.field-value {
  color: var(--stk-text-strong);
  font-weight: 500;
  word-break: break-word;
}
</style> 